import {
	CubesAvailable,
	EditTruckDetailsFlowButtons,
	EditTruckDetailsWrapper,
	TruckDetailsContent,
	TruckDetailsHeader,
	TruckDetailsSummary,
	TruckDetailsSummaryAccountNumber,
	TruckDetailsSummaryCabinetCount,
	TruckDetailsSummaryDetails,
	TruckDetailsSummaryDetailsItemLabel,
	TruckDetailsSummaryDetailsItemValue,
	TruckDetailsSummaryNote,
	TruckDetailsSummaryProductLine,
	TruckDetailsSummaryRequestDate,
	TruckDetailsSummarySkeleton,
	TruckDetailsWrapper,
	TruckName,
	TruckNameWrapper
} from "./ReviewTruckDetails.styles";
import {
	AccountNumberText,
	CabinetCountText,
	CancelButtonText,
	EditTruckDetailsText,
	ProductLineText,
	RequestDateText,
	ReviewTruckDetailsNote,
	SaveButtonText,
	TruckNameText,
	TruckNumberPending,
	UnavailableDataPlaceholderText,
	UniqueTruckErrorText
} from "constants/text";
import { dateFormatTwoDigitDays, isDateWithinBusinessDayRange } from "utils/date";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store.tsx";
import { ReactNode, useEffect, useState } from "react";
import { addBusinessDays } from "date-fns";
import {
	END_BUSINESS_DAY_RANGE,
	requestDateValidator,
	START_BUSINESS_DAY_RANGE
} from "components/Trucks/stepOneSchema";
import { Controller, useForm } from "react-hook-form";
import { truckActions } from "features/reducers/truck/truck";
import { Button, FormHelperText, TextField } from "@mui/material";
import { getFormFieldProps } from "utils/form";
import DatePickerDateField from "components/DatePicker/components/DatePickerDateField";
import { RequestDateWrapper } from "components/Trucks/TruckShippingInfo/TruckShippingInfo.styles";
import HelperText from "components/Form/HelperText/HelperText";
import { yupResolver } from "@hookform/resolvers/yup";
import { truckDetailsSchema } from "components/Trucks/truckDetailsSchema";
import { useSaveTruckMutation } from "features/api/truckApi";
import TruckCubeCountTracker from "components/Trucks/TruckCubeCountTracker/TruckCubeCountTracker";
import { useUserLocation } from "hooks/useUserLocation.ts";

export type ReviewTruckDetailsErrorTypes = "apiError" | "invalidDate" | "truckNameAlreadyExists";

interface ReviewTruckDetailsProps {
	editable?: boolean;
	onEditStateChange?: (editing: boolean) => void;
	onError?: (
		errors: {
			message?: string;
			title?: string;
			status?: string;
			type?: ReviewTruckDetailsErrorTypes;
		}[]
	) => void;
	onClearError?: (errors?: ReviewTruckDetailsErrorTypes[]) => void;
}

const ReviewTruckDetails = ({
	editable,
	onEditStateChange = (editing: boolean) => {},
	onError = (error: any) => {},
	onClearError = () => {}
}: ReviewTruckDetailsProps) => {
	const { isConfirmationPage } = useUserLocation();

	const truck = useSelector((state: RootState) => state.truck.truck);

	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [saveTruck, { isLoading: isSavingTruck }] = useSaveTruckMutation();

	const {
		trigger: triggerForm,
		setValue,
		getValues,
		handleSubmit,
		watch,
		formState,
		register,
		clearErrors,
		control,
		setError
	} = useForm({
		resolver: yupResolver(truckDetailsSchema),
		defaultValues: {
			truckName: undefined,
			requestDate: undefined
		}
	});

	useEffect(() => {
		requestDateValidator.validate(truck?.requestedDeliveryDate).catch((rejection) => {
			if (!isEditing) {
				truck?.requestedDeliveryDate && setValue("requestDate", truck.requestedDeliveryDate);
				truck?.truckName && setValue("truckName", truck.truckName);

				triggerForm();
				editable && handleEditDetailsOnClick();

				// Note that the API prevents saving of trucks with non-unique
				// names.  If there is a rejection here from the validator, then
				// it _always_ is delivery date, until this form gets more
				// fields.
				onError([
					{
						type: "invalidDate"
					}
				]);
			}
		});
		// Only run on component mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const dispatch = useDispatch();

	const today = new Date();
	const firstValidDate = addBusinessDays(today, START_BUSINESS_DAY_RANGE);
	const secondValidDate = addBusinessDays(today, END_BUSINESS_DAY_RANGE);

	const handleEditDetailsOnClick = () => {
		setIsEditing(true);
		onEditStateChange(true);
		setValue("truckName", truck?.truckName ?? "");
		setValue("requestDate", truck?.requestedDeliveryDate ?? "");
		triggerForm();
	};

	const handleCancelOnClick = () => {
		setIsEditing(false);
		onEditStateChange(false);

		// Reset values to their original ones
		setValue("truckName", truck?.truckName ?? "");
		setValue("requestDate", truck?.requestedDeliveryDate ?? "");

		// Clear related errors from any parent components listening to the callback
		onClearError(["truckNameAlreadyExists"]);

		// Clear errors in the form state
		clearErrors();
	};

	const handleSubmitForm = async () => {
		// Save truck
		if (getValues) {
			try {
				const { requestDate, truckName } = getValues();
				await saveTruck({
					...truck,
					requestedDeliveryDate: requestDate,
					truckName: truckName
				})
					.unwrap()
					.then(async (response) => {
						if (response) {
							dispatch(
								truckActions.updateTruck({
									...truck,
									truckName: response.truckName,
									requestedDeliveryDate: response.requestedDeliveryDate
								})
							);

							setIsEditing(false);
							onEditStateChange(false);
							onClearError();
						} else {
							throw Error();
						}
					});
			} catch (error: any) {
				if (error?.status === 409) {
					setError("truckName", { message: UniqueTruckErrorText });
					onError([{ type: "truckNameAlreadyExists" }]);
				} else {
					onError([{ type: "apiError" }]);
				}
			}
		}
	};

	let truckDetailsSummaryDetailsContent: ReactNode;

	if (isSavingTruck) {
		truckDetailsSummaryDetailsContent = (
			<TruckDetailsSummaryDetails>
				<TruckDetailsSummaryRequestDate>
					<TruckDetailsSummarySkeleton variant="rounded" />
				</TruckDetailsSummaryRequestDate>
				<TruckDetailsSummaryAccountNumber>
					<TruckDetailsSummarySkeleton variant="rounded" />
				</TruckDetailsSummaryAccountNumber>
				<TruckDetailsSummaryProductLine>
					<TruckDetailsSummarySkeleton variant="rounded" />
				</TruckDetailsSummaryProductLine>
				<TruckDetailsSummaryCabinetCount>
					<TruckDetailsSummarySkeleton variant="rounded" />
				</TruckDetailsSummaryCabinetCount>
				<CubesAvailable>
					<TruckDetailsSummarySkeleton variant="rounded" />
				</CubesAvailable>
			</TruckDetailsSummaryDetails>
		);
	} else {
		// !isSavingTruck
		truckDetailsSummaryDetailsContent = (
			<TruckDetailsSummaryDetails data-testid="truck-details-summary-details">
				<TruckDetailsSummaryRequestDate>
					<TruckDetailsSummaryDetailsItemLabel>{RequestDateText}</TruckDetailsSummaryDetailsItemLabel>
					<TruckDetailsSummaryDetailsItemValue data-testid="truck-details-request-date">
						{truck?.requestedDeliveryDate
							? dateFormatTwoDigitDays(truck.requestedDeliveryDate)
							: UnavailableDataPlaceholderText}
					</TruckDetailsSummaryDetailsItemValue>
				</TruckDetailsSummaryRequestDate>
				<TruckDetailsSummaryAccountNumber>
					<TruckDetailsSummaryDetailsItemLabel>{AccountNumberText}</TruckDetailsSummaryDetailsItemLabel>
					<TruckDetailsSummaryDetailsItemValue data-testid="truck-details-account-number">
						{truck?.accountNumber ?? UnavailableDataPlaceholderText} -{" "}
						{truck?.accountName ?? UnavailableDataPlaceholderText}
					</TruckDetailsSummaryDetailsItemValue>
				</TruckDetailsSummaryAccountNumber>
				<TruckDetailsSummaryProductLine>
					<TruckDetailsSummaryDetailsItemLabel>{ProductLineText}</TruckDetailsSummaryDetailsItemLabel>
					<TruckDetailsSummaryDetailsItemValue data-testid="truck-details-product-line">
						{truck?.productLine?.description ?? UnavailableDataPlaceholderText}
					</TruckDetailsSummaryDetailsItemValue>
				</TruckDetailsSummaryProductLine>
				<TruckDetailsSummaryCabinetCount>
					<TruckDetailsSummaryDetailsItemLabel>{CabinetCountText}</TruckDetailsSummaryDetailsItemLabel>
					<TruckDetailsSummaryDetailsItemValue data-testid="truck-details-cabinet-count">
						{truck?.cabinetCount ?? UnavailableDataPlaceholderText}
					</TruckDetailsSummaryDetailsItemValue>
				</TruckDetailsSummaryCabinetCount>
				<CubesAvailable>
					<TruckCubeCountTracker truck={truck} />
				</CubesAvailable>
			</TruckDetailsSummaryDetails>
		);
	}

	let truckDetailsContent: ReactNode;

	if (isEditing) {
		if (isSavingTruck) {
			truckDetailsContent = (
				<>
					{truckDetailsSummaryDetailsContent}
					<EditTruckDetailsWrapper>
						<TruckDetailsSummarySkeleton variant="rounded" />
						<TruckDetailsSummarySkeleton variant="rounded" />
						<TruckDetailsSummarySkeleton variant="rounded" />
					</EditTruckDetailsWrapper>
				</>
			);
		} else {
			truckDetailsContent = (
				<form onSubmit={handleSubmit(handleSubmitForm)}>
					{truckDetailsSummaryDetailsContent}
					<EditTruckDetailsWrapper>
						<TruckNameWrapper>
							<TextField
								label={TruckNameText}
								size="small"
								defaultValue={truck?.truckName}
								{...getFormFieldProps({
									name: "truckName",
									characterLimit: {
										currentLength: watch("truckName")?.length ?? 0,
										limit: 15
									},
									formState: formState,
									register: register,
									schema: truckDetailsSchema
								})}
								data-testid="edit-truck-details-truckName"
								fullWidth
							/>
						</TruckNameWrapper>
						<Controller
							control={control}
							name="requestDate"
							defaultValue={truck?.requestedDeliveryDate}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<RequestDateWrapper isError={!!error}>
									<DatePickerDateField
										data-testid="edit-truck-info-requestedDate"
										label={RequestDateText + " *"}
										value={value ? new Date(value) : undefined}
										firstValidDate={firstValidDate}
										secondValidDate={secondValidDate}
										onChange={(value) => {
											onChange(value);
											if (
												isDateWithinBusinessDayRange(
													new Date(value),
													START_BUSINESS_DAY_RANGE,
													END_BUSINESS_DAY_RANGE
												)
											) {
												clearErrors("requestDate");
											}
										}}
										isEndIcon
										valueCanBeNull
									/>
									{error && (
										<FormHelperText error>
											<HelperText
												isError={true}
												text={error?.message}
											/>
										</FormHelperText>
									)}
								</RequestDateWrapper>
							)}
						/>
					</EditTruckDetailsWrapper>
					<EditTruckDetailsFlowButtons data-testid="flow-buttons">
						<Button
							data-testid="edit-truck-details-cancel-button"
							variant="text"
							onClick={handleCancelOnClick}
						>
							{CancelButtonText}
						</Button>
						<Button
							data-testid="edit-truck-details-save-button"
							variant="contained"
							type="submit"
							disabled={false}
						>
							{SaveButtonText}
						</Button>
					</EditTruckDetailsFlowButtons>
				</form>
			);
		}
	} else {
		// !isEditing
		truckDetailsContent = (
			<>
				<TruckDetailsSummary data-testid="truck-details-summary">
					{truckDetailsSummaryDetailsContent}
				</TruckDetailsSummary>
				<TruckDetailsSummaryNote data-testid="truck-details-summary-note">
					{ReviewTruckDetailsNote}
				</TruckDetailsSummaryNote>
			</>
		);
	}

	return (
		<TruckDetailsWrapper data-testid="trucks-review-truck-details">
			<TruckDetailsHeader data-testid="trucks-review-truck-details-header">
				<TruckName data-testid="truck-name">{truck?.truckName ?? UnavailableDataPlaceholderText}</TruckName>
				{isConfirmationPage && <p>{TruckNumberPending}</p>}
				{editable && !isEditing && (
					<Button
						onClick={handleEditDetailsOnClick}
						variant="outlined"
						data-testid="edit-truck-details-button"
					>
						{EditTruckDetailsText}
					</Button>
				)}
			</TruckDetailsHeader>
			<TruckDetailsContent data-testid="truck-details-content">{truckDetailsContent}</TruckDetailsContent>
		</TruckDetailsWrapper>
	);
};

export default ReviewTruckDetails;
